/**
 * Concurrencia · `BFF_BO.md` §3.8
 *
 * Las entidades que dos personas pueden editar a la vez llevan `ETag`, y en
 * `students` la precondición **no es opcional**:
 *
 * ```
 * GET   …/students/{id}                      → ETag: W/"1789239751860"
 * PATCH …/students/{id}  sin If-Match        → 428  (falta la precondición)
 * PATCH …/students/{id}  con uno viejo       → 412  (alguien lo cambió)
 * PATCH …/students/{id}  con el bueno        → 200  + ETag NUEVO
 * ```
 *
 * Que el `PATCH` devuelva el `ETag` nuevo es lo que permite seguir editando sin
 * volver a pedir la ficha.
 *
 * **Y un `412` no se enseña en rojo.** Es «el dueño acaba de cambiar esto desde su
 * móvil»: hay que recargar, **enseñar qué cambió** y dejar reintentar. De ahí
 * `camposQueCambiaron`, que es lo que hace posible el «qué cambió» sin inventarse
 * nada.
 */

import type { Problema } from '@/shared/api/errores';

export const esConflictoDeEdicion = (problema: Problema): boolean =>
  problema.status === 412;

/** Falta el `If-Match`. Si sale, es un fallo nuestro, no del usuario. */
export const faltaLaPrecondicion = (problema: Problema): boolean =>
  problema.status === 428;

/** Lee el `ETag` de una respuesta de Eden. */
export const etagDe = (r: { response?: Response | undefined }): string | null =>
  r.response?.headers?.get('etag') ?? null;

/**
 * Lo que no se cuenta como «cambió alguien».
 *
 * `updated_at` cambia **siempre** que alguien edita, así que aparecería en todos
 * los conflictos y no dice nada: la lista útil es «el apellido», no «el apellido
 * y la marca de tiempo que se actualiza sola». Los identificadores no cambian
 * nunca, y si cambiaran no sería un conflicto sino otra ficha.
 */
const METADATOS = new Set(['updated_at', 'created_at', 'id', 'academy_id']);

/**
 * Qué campos cambió la otra persona.
 *
 * Compara **lo que había cuando abrí el formulario** con lo que hay ahora en el
 * servidor. Los campos que yo he tocado no entran: de esos ya sé que cambian, y
 * mezclarlos daría una lista de «cambios» que son míos.
 *
 * Se comparan por texto a propósito: un `Date` y otro `Date` con el mismo
 * instante son objetos distintos, y `!==` los daría por diferentes siempre.
 */
export function camposQueCambiaron<T extends Record<string, unknown>>(
  base: T,
  ahora: T,
  camposTocados: readonly string[] = []
): readonly string[] {
  const tocados = new Set(camposTocados);

  return Object.keys(ahora).filter((campo) => {
    if (tocados.has(campo) || METADATOS.has(campo)) return false;
    return comparable(base[campo]) !== comparable(ahora[campo]);
  });
}

/**
 * Un valor, reducido a algo que se pueda comparar con `!==`.
 *
 * Nada de `String(valor)` a secas: un objeto daría `[object Object]` y dos
 * objetos distintos saldrían iguales, que es justo lo contrario de lo que hace
 * falta aquí.
 */
const comparable = (valor: unknown): string => {
  if (valor === null || valor === undefined) return '';
  if (valor instanceof Date) return valor.toISOString();
  if (typeof valor === 'string') return valor;
  if (typeof valor === 'number' || typeof valor === 'boolean')
    return String(valor);
  // Un anidado —`settings`, una lista de grupos— se compara por su forma.
  return JSON.stringify(valor) ?? '';
};
